import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BillingSettings from './BillingSettings';
import { getStoredUser, StoredUser, refreshUserProfile } from '../services/subscriptionService';
import layoutStyles from '../styles/modules/PageLayout.module.css';
import headerStyles from '../styles/modules/Header.module.css';
import cardStyles from '../styles/modules/Card.module.css';
import buttonStyles from '../styles/modules/Button.module.css';
import styles from '../styles/modules/Settings.module.css';

type TabKey = 'account' | 'packages';

const Settings: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<TabKey>('account');
    const [user, setUser] = useState<StoredUser | null>(() => getStoredUser());

    const formatDateTime = (value?: string | null): string => {
        if (!value) {
            return '';
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) {
            return value;
        }

        return date.toLocaleString();
    };

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        if (!token) {
            navigate('/signin');
            return;
        }

        refreshUserProfile(token)
            .then((profile) => {
                setUser(profile);
            })
            .catch((error) => {
                console.error('Error refreshing user profile in settings:', error);
            });
    }, [navigate]);

    return (
        <div className={layoutStyles.page}>
            <header className={headerStyles.header}>
                <button
                    className={`${headerStyles.backButton} ${headerStyles.backButtonAbsolute}`}
                    onClick={() => navigate('/dashboard')}
                    title={t('common.backToDashboard')}
                    aria-label={t('common.backToDashboard')}
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7" />
                    </svg>
                </button>
                <h1 className={headerStyles.title}>{t('settings.title') || 'Settings'}</h1>
                <div></div>
            </header>

            <div className={`${layoutStyles.container} ${styles.backgroundContainer}`}>
                <div className={layoutStyles.content}>
                    <div className={cardStyles.cardGlass}>
                        <div className={styles.tabs}>
                            <button
                                className={`${buttonStyles.tab} ${activeTab === 'account' ? buttonStyles.active : ''}`}
                                onClick={() => setActiveTab('account')}
                            >
                                {t('settings.accountTab') || 'Account'}
                            </button>
                            <button
                                className={`${buttonStyles.tab} ${activeTab === 'packages' ? buttonStyles.active : ''}`}
                                onClick={() => setActiveTab('packages')}
                            >
                                {t('settings.billingTab') || 'Packages'}
                            </button>
                        </div>

                        {activeTab === 'account' && (
                            <div className={styles.accountSection}>
                                <h3>{t('settings.accountTitle') || 'Account'}</h3>
                                <div className={styles.creditBlock}>
                                    <h4>{t('settings.availableCredits') || 'Available credits'}</h4>

                                    <div className={styles.creditRow}>
                                        <span>{t('subscription.jobPostCredits') || 'Job Post credits'}:</span>
                                        <strong>{user?.jobPostsCount ?? 0}</strong>
                                    </div>

                                    <div className={styles.creditRow}>
                                        <span>{t('subscription.quickSearchCredits') || 'Quick Search credits'}:</span>
                                        <strong>{user?.quickSearchCount ?? 0}</strong>
                                    </div>

                                    {user?.activeQuickSearchExpiry && (
                                        <div className={styles.creditRow}>
                                            <span>{t('subscription.quickSearchActiveUntil') || 'Quick Search active until'}:</span>
                                            <strong>{formatDateTime(user.activeQuickSearchExpiry)}</strong>
                                        </div>
                                    )}
                                </div>

                                    <p>{t('settings.accountHint') || 'Use profile page to manage personal information.'}</p>
                                    <button type="button" className={buttonStyles.primary} onClick={() => navigate('/profile')}>
                                        {t('settings.openProfile') || 'Open Profile'}
                                    </button>
                            </div>
                        )}
                    </div>

                    {activeTab === 'packages' && <BillingSettings />}
                </div>
            </div>
        </div>
    );
};

export default Settings;
